import { useQuery } from "@tanstack/react-query";
import { Building2, ChevronLeft, ChevronRight, Loader2, MapPin, Search, Users } from "lucide-react";
import { Link, useNavigation, useSearchParams } from "react-router";
import { CompanyAutocomplete } from "~/components/search/company-autocomplete";
import { DataLoadError } from "~/components/ui/data-load-error";
import { Skeleton } from "~/components/ui/skeleton";
import { companySlug } from "~/lib/company-profile";
import {
	type CompanySearchResult,
	fetchCompanySearch,
	MIN_COMPANY_SEARCH_LENGTH,
	shouldRetryCompanySearch,
} from "~/lib/company-search";
import { regionToSlug } from "~/lib/directory-utils";
import { generateMetaTags, SITE_URL } from "~/lib/seo";
import type { Route } from "./+types/search";

export function meta({ location }: Route.MetaArgs) {
	const params = new URLSearchParams(location.search);
	const q = params.get("q");

	const title = q ? `${q} – Výsledky hledání | FirmIndex` : "Vyhledávání firem | FirmIndex";
	const description = q
		? `Výsledky vyhledávání pro "${q}". Najděte finanční data, obchodní rejstřík a další informace o českých firmách.`
		: "Vyhledávejte české firmy podle názvu nebo IČO. Obchodní rejstřík, finanční údaje a další informace.";
	const canonical = q ? `${SITE_URL}/search?q=${encodeURIComponent(q)}` : `${SITE_URL}/search`;

	return generateMetaTags({ title, description, canonical });
}

const STATUS_LABELS: Record<string, { label: string; className: string }> = {
	ACTIVE: { label: "Aktivní", className: "badge badge-soft badge-success" },
	DISSOLVED: { label: "Zrušená", className: "badge badge-soft badge-error" },
	IN_LIQUIDATION: { label: "V likvidaci", className: "badge badge-soft badge-warning" },
	IN_INSOLVENCY: { label: "V insolvenci", className: "badge badge-soft badge-error" },
	SUSPENDED: { label: "Pozastavená", className: "badge badge-soft badge-warning" },
};

function CompanyCard({ company, isPending }: { company: CompanySearchResult; isPending: boolean }) {
	const status = STATUS_LABELS[company.status] ?? {
		label: company.status,
		className: "badge badge-soft",
	};
	const rSlug = company.region ? regionToSlug(company.region) : null;

	return (
		<div
			className="flex flex-col gap-3 card-surface rounded-xl p-4 transition-[box-shadow,opacity] hover:shadow-md"
			aria-busy={isPending}
		>
			<Link
				to={`/firma/${company.ico}-${companySlug(company.name)}`}
				prefetch="intent"
				className="flex items-start justify-between gap-3"
			>
				<div className="flex flex-col gap-1">
					<h3 className="text-sm font-semibold">{company.name}</h3>
					<p className="text-xs text-muted-foreground">
						IČO: <span className="font-mono">{company.ico}</span>
						{company.legalForm && <> | {company.legalForm}</>}
					</p>
				</div>
				<div className="flex items-center gap-2">
					{isPending && (
						<Loader2 className="h-4 w-4 animate-spin text-primary" aria-label="Načítám firmu" />
					)}
					<span className={status.className}>{status.label}</span>
				</div>
			</Link>
			<div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
				{company.city && (
					<span className="flex items-center gap-1">
						<MapPin className="h-3 w-3" />
						{company.city}
						{company.region && rSlug ? (
							<>
								,{" "}
								<Link to={`/directory/kraj/${rSlug}`} className="text-primary hover:underline">
									{company.region}
								</Link>
							</>
						) : (
							company.region && <>, {company.region}</>
						)}
					</span>
				)}
				{company.naceDescription && (
					<span className="flex items-center gap-1">
						<Building2 className="h-3 w-3" />
						{company.naceCode ? (
							<Link
								to={`/directory/odvetvi/${company.naceCode}`}
								className="text-primary hover:underline"
							>
								{company.naceDescription}
							</Link>
						) : (
							company.naceDescription
						)}
					</span>
				)}
				{company.employeeLabel && (
					<span className="flex items-center gap-1">
						<Users className="h-3 w-3" />
						{company.employeeLabel}
					</span>
				)}
			</div>
		</div>
	);
}

function ResultSkeleton() {
	return (
		<div className="flex flex-col gap-3 card-surface rounded-xl p-4">
			<div className="flex items-start justify-between">
				<div className="flex flex-col gap-1">
					<Skeleton className="h-4 w-48" />
					<Skeleton className="h-3 w-32" />
				</div>
				<Skeleton className="h-5 w-16 rounded-full" />
			</div>
			<div className="flex gap-4">
				<Skeleton className="h-3 w-24" />
				<Skeleton className="h-3 w-32" />
			</div>
		</div>
	);
}

const PAGE_LIMIT = 20;

export default function SearchPage() {
	const [searchParams, setSearchParams] = useSearchParams();
	const q = searchParams.get("q") ?? "";
	const page = Number(searchParams.get("page") ?? "1");
	const canSearch = q.trim().length >= MIN_COMPANY_SEARCH_LENGTH;

	const navigation = useNavigation();

	const { data, status, fetchStatus, error, refetch } = useQuery({
		queryKey: ["companySearch", q, page],
		queryFn: ({ signal }) => fetchCompanySearch(q, page, PAGE_LIMIT, signal),
		enabled: canSearch,
		retry: shouldRetryCompanySearch,
		staleTime: 60_000,
	});
	const isSearching = fetchStatus === "fetching";
	const pendingCompanyIco = navigation.location?.pathname.match(/^\/firma\/(\d+)/)?.[1] ?? null;

	const totalPages = data ? Math.ceil(data.total / PAGE_LIMIT) : 0;

	const goToPage = (newPage: number) => {
		setSearchParams({ q, page: String(newPage) });
	};

	return (
		<div className="min-h-screen">
			{/* Search bar */}
			<div className="px-4 pt-6">
				<div className="glass-panel-strong mx-auto max-w-4xl rounded-2xl p-3 sm:p-4">
					<CompanyAutocomplete
						formLabel="Vyhledávání ve výsledcích"
						initialValue={q}
						isSearching={isSearching}
						onSearch={(value) => setSearchParams({ q: value, page: "1" })}
						placeholder="Hledejte firmy podle názvu nebo IČO…"
						showError={status !== "error"}
					/>
				</div>
			</div>

			{/* Content */}
			<main className="mx-auto max-w-4xl px-4 py-6">
				<div className="flex flex-col gap-4">
					{/* Results count */}
					{canSearch && data && (
						<p className="text-sm text-muted-foreground">
							{data.total === 0
								? "Žádné výsledky"
								: `${data.totalIsCapped ? "Více než " : ""}${data.total.toLocaleString("cs-CZ")} ${data.total === 1 ? "výsledek" : data.total < 5 ? "výsledky" : "výsledků"}`}
							{` pro \u201E${q}\u201C`}
						</p>
					)}

					{/* Loading */}
					{canSearch && status === "pending" && isSearching && (
						<div className="flex flex-col gap-3">
							{["s1", "s2", "s3", "s4", "s5"].map((key) => (
								<ResultSkeleton key={key} />
							))}
						</div>
					)}

					{/* Error */}
					{canSearch && status === "error" && (
						<DataLoadError
							title="Výsledky se nepodařilo načíst"
							description={(error as Error).message}
							onRetry={() => refetch()}
							retrying={isSearching}
						/>
					)}

					{/* Empty state */}
					{!canSearch && (
						<div className="flex flex-col items-center gap-2 py-16 text-center">
							<Search className="h-12 w-12 text-muted-foreground" />
							<p className="text-sm text-muted-foreground">
								Zadejte alespoň tři znaky názvu firmy nebo IČO
							</p>
						</div>
					)}

					{/* No results */}
					{canSearch && data && data.total === 0 && (
						<div className="flex flex-col items-center gap-2 py-16 text-center">
							<Building2 className="h-12 w-12 text-muted-foreground" />
							<p className="text-sm font-medium">Nic jsme nenašli</p>
							<p className="text-sm text-muted-foreground">
								Zkuste upravit hledaný výraz nebo zkontrolovat pravopis
							</p>
						</div>
					)}

					{/* Results */}
					{canSearch && data && data.results.length > 0 && (
						<div className="flex flex-col gap-3">
							{data.results.map((company) => (
								<CompanyCard
									key={company.id}
									company={company}
									isPending={pendingCompanyIco === company.ico}
								/>
							))}
						</div>
					)}

					{/* Pagination */}
					{canSearch && totalPages > 1 && (
						<div className="flex items-center justify-center gap-2 py-4">
							<button
								type="button"
								className="btn btn-ghost btn-sm"
								disabled={page <= 1}
								onClick={() => goToPage(page - 1)}
							>
								<ChevronLeft className="h-4 w-4" />
								Předchozí
							</button>
							<span className="text-sm text-muted-foreground">
								Strana {page} z {totalPages}
							</span>
							<button
								type="button"
								className="btn btn-ghost btn-sm"
								disabled={page >= totalPages}
								onClick={() => goToPage(page + 1)}
							>
								Další
								<ChevronRight className="h-4 w-4" />
							</button>
						</div>
					)}
				</div>
			</main>
		</div>
	);
}
